import jsPDF from "jspdf";
import L from "leaflet";
import html2canvas from "html2canvas";

/* =========================================================
   DATE FORMAT HELPERS
   ========================================================= */
const formatDateTime = (date) => {
  if (!date) return "-";
  const d = new Date(date);
  if (Number.isNaN(d.getTime())) return "-";
  return d.toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true
  });
};

/* =========================================================
   CONSTANTS (LAYOUT)
   ========================================================= */
const PAGE_W = 210;
const PAGE_H = 297;

const MARGIN_L = 12;
const MARGIN_R = 12;
const TOP_FIRST = 44; // first page content start (after banner)
const TOP_OTHER = 16; // other pages start (no repeating header)
const BOTTOM = 22;

const CONTENT_W = PAGE_W - MARGIN_L - MARGIN_R;

/* =========================================================
   COLOR PALETTE (BRAND-LIKE)
   ========================================================= */
const COLORS = {
  navy: [30, 42, 71],
  slate: [52, 73, 94],
  lightBg: [246, 248, 252],
  line: [220, 226, 235],

  green: [109, 158, 91],
  red: [224, 102, 102],
  amber: [249, 168, 37],

  text: [25, 30, 40],
  muted: [110, 120, 135],
  white: [255, 255, 255]
};

/* =========================================================
   PUBLIC API
   ========================================================= */
export const downloadVerificationAnalyticsReport = async (rawRows, skuFilterList = [], selectedProduct, start, end, verLogs) => {
  const doc = new jsPDF("p", "mm", "a4");

  // state
  let y = 0;

  const setY = (v) => { y = v; };
  const getY = () => y;

  const safeAddPage = () => {
    doc.addPage();
    setY(TOP_OTHER);
  };

  const ensureSpace = (h) => {
    if (getY() + h > PAGE_H - BOTTOM) {
      safeAddPage();
      return true;
    }
    return false;
  };

  const ensureSectionSpace = (titleH, firstBlockH) => {
    // Prevent orphan title at bottom
    if (getY() + titleH + firstBlockH > PAGE_H - BOTTOM) safeAddPage();
  };

  // normalize + filter
  const rows = Array.isArray(rawRows) ? rawRows : [];
  const skuSet = new Set((skuFilterList || []).filter(Boolean));

  const filtered = skuSet.size
    ? rows.filter(r => skuSet.has(r.sku_id))
    : rows;

  // compute aggregates
  const stats = computeStats(filtered);

  // banner header (first page only)
  drawFirstPageBanner(doc, stats, selectedProduct, start, end);

  setY(TOP_FIRST);

  // Cover-like intro block
  ensureSpace(30);
  // drawIntroBlock(doc, stats, getY());
  // setY(getY() + 52);

  // KPI cards + donut
  ensureSectionSpace(10, 55);
  drawSectionTitle(doc, "Executive Dashboard", ensureSpace, getY, setY);
  drawKpiCards(doc, stats, getY());
  // drawDonutWithLegend(doc, stats, MARGIN_L + 95, getY() + 45);
  setY(getY() + 28);

  // Insight callouts (auto narrative)
  ensureSectionSpace(10, 28);


  const heatmapImage = await generatePDF(filtered);
  drawSectionTitle(doc, "Verification Analytics", ensureSpace, getY, setY);
  doc.addImage(
    heatmapImage,
    "PNG",
    MARGIN_L,
    getY() - 5,
    CONTENT_W,
    180
  );

  setY(getY() + 175);

  // Charts row: Top Products + Top Countries counterfeit
  if (selectedProduct === "all") {
    ensureSectionSpace(10, 70);
    drawSectionTitle(doc, "Verification Overview", ensureSpace, getY, setY);

    const topSkus = Object.entries(stats.bySku).map(([name, v]) => ({
      name,
      ...v
    }));
    const topCountries = buildTopEntities(stats.byCountry, 6, (v) => v.counterfeit);

    drawHorizontalStackedBarChart(doc, "Products by Scans", topSkus, MARGIN_L, getY(), 180);

    setY(getY() + topSkus.length * 10.5 + 8);
    // Summary tables (visually formatted, color-coded risk)
    ensureSectionSpace(10, 30);
    drawSectionTitle(doc, "Product Verification Summary", ensureSpace, getY, setY);

    // Product table (compact)
    renderTable(doc, {
      title: "Product Summary (Validated / Not Validated)",
      headers: ["Product Name", "Total Scans", "Validated", "Not Validated"],
      rows: buildSummaryRows(stats.bySku, 15, "sku"),
      colW: [38, 30, 30, 30],
      fontSize: 9,
      zebra: true,
      riskColoring: true
    }, ensureSpace, getY, setY);
  }
  // Drill-down detail section (not on page 1 if it becomes too long)
  if (verLogs) {
    ensureSectionSpace(10, 24);
    drawSectionTitle(doc, "Detailed Verification Logs", ensureSpace, getY, setY);

    const sortedFiltered = [...filtered].sort(
      (a, b) => new Date(b.created_at) - new Date(a.created_at)
    );

    const detailRows = sortedFiltered.map(r => ([
      safeStr(r.sku_id),
      safeStr(r.uid),
      safeStr(r.country),
      safeStr(r.state),
      safeStr(r.district),
      resultLabel(r.result),
      new Date(r.created_at).toLocaleString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hour12: true
      })
    ]));


    renderTable(doc, {
      title: "Verification Log (Product => QR => Location)",
      headers: ["Product", "QR", "Country", "State", "District", "Result", "Time"],
      rows: detailRows,
      colW: [18, 22, 24, 22, 22, 22, 35],
      fontSize: 8,
      zebra: true,
      riskColoring: false
    }, ensureSpace, getY, setY);
  }


  ensureSectionSpace(10, 25);
  // footer
  addFooter(doc);

  doc.setTextColor(0);
  doc.text("End of Report", 105, getY() + 20, { align: "center" });

  doc.text('__________________________', 150, getY() + 35);
  doc.text('Authorized Signatory', 160, getY() + 40);

  // open
  window.open(doc.output("bloburl"), "_blank");
};

/* =========================================================
   CORE STATS
   ========================================================= */
const computeStats = (rows) => {
  const stats = {
    total: 0,
    genuine: 0,
    counterfeit: 0,

    bySku: {},
    byContinent: {},
    byCountry: {},
    byState: {},
    byDistrict: {},

    // derived
    genuinePct: 0,
    counterfeitPct: 0,
  };

  rows.forEach(r => {
    stats.total += 1;

    const sku = safeStr(r.sku_id) || "Unknown Product";
    const cont = safeStr(r.continent) || "Unknown";
    const ctry = safeStr(r.country) || "Unknown";
    const state = safeStr(r.state) || "Unknown";
    const dist = safeStr(r.district) || "Unknown";

    const bucket = resultBucket(r.result);

    // totals
    if (bucket === "genuine") stats.genuine += 1;
    else if (bucket === "counterfeit") stats.counterfeit += 1;

    // maps
    bump(stats.bySku, sku, bucket);
    bump(stats.byContinent, cont, bucket);
    bump(stats.byCountry, ctry, bucket);
    bump(stats.byState, state, bucket);
    bump(stats.byDistrict, dist, bucket);
  });

  const denom = stats.total || 1;
  stats.genuinePct = Math.round((stats.genuine / denom) * 100);
  stats.counterfeitPct = Math.round((stats.counterfeit / denom) * 100);

  // extra derived: peaks
  stats.peakSku = peakKey(stats.bySku, v => v.total);
  stats.peakCountry = peakKey(stats.byCountry, v => v.counterfeit);
  stats.peakContinent = peakKey(stats.byContinent, v => v.total);
  //stats inside peak country where country = peak country
  stats.peakCountryState = peakKey(stats.byState, v => v.total);
  stats.peakCountryStateDistrict = peakKey(stats.byDistrict, v => v.total);

  return stats;
};

const bump = (map, key, bucket) => {
  if (!map[key])
    map[key] = { total: 0, genuine: 0, counterfeit: 0, unknown: 0 };

  map[key].total += 1;

  if (!map[key][bucket]) map[key][bucket] = 0;
  map[key][bucket] += 1;
};

const peakKey = (map, scoreFn) => {
  let bestK = "-";
  let bestV = -Infinity;
  Object.entries(map).forEach(([k, v]) => {
    const s = scoreFn(v);
    if (s > bestV) {
      bestV = s;
      bestK = k;
    }
  });
  return bestK;
};

const resultBucket = (result) => {
  const n = parseInt(result, 10);

  if (n === 1) return "genuine";
  if (n === 0) return "counterfeit";

  return "unknown";
};

const resultLabel = (result) => {
  const b = resultBucket(result);

  if (b === "genuine") return "Validated";
  if (b === "counterfeit") return "Not Validated";

  return "Unknown";
};

const safeStr = (v) => (v === null || v === undefined ? "" : String(v).trim());

/* =========================================================
   VISUAL COMPONENTS
   ========================================================= */
const drawFirstPageBanner = (doc, stats, selectedProduct, start, end) => {
  // top band
  const companyLogoBase64 = "logo.png";

  // Header with logo
  doc.setFillColor(255, 255, 255);
  doc.rect(0, 0, 210, 35, 'F');

  // Add company logo if available
  if (companyLogoBase64) {
    try {
      doc.addImage(companyLogoBase64, 'PNG', 8, 5, 30, 25);
      doc.addImage("QRmor.jpeg", "PNG", 175, 5, 30, 25);
    } catch (e) {
      console.error('Error adding company logo:', e);
    }
  }
  // title
  doc.setTextColor(...COLORS.text);
  doc.setFontSize(18);
  doc.setFont(undefined, "bold");
  doc.text("QRmor VERIFICATION ANALYTICS REPORT", PAGE_W / 2 + 3, 16, { align: "center" });

  doc.setFontSize(9);
  doc.setFont(undefined, "normal");
  doc.text(
    `Generated: ${formatDateTime(new Date())} | ${sessionStorage.getItem("name") || ""} ${sessionStorage.getItem("isAdmin") !== 'true' ? "| " + sessionStorage.getItem("group_id") : ""}`,
    PAGE_W / 2 + 3,
    24,
    { align: "center" }
  );

  if (selectedProduct !== "all") {
    doc.text(
      "Product Name: " + selectedProduct,
      PAGE_W / 2 + 3,
      30,
      { align: "center" }
    );
  }
  doc.text(
    "Date Range: " + formatDateTime(start) + " to " + formatDateTime(end),
    PAGE_W / 2 + 3,
    36,
    { align: "center" }
  );

  doc.setTextColor(...COLORS.text);
};

const drawIntroBlock = (doc, stats, y) => {
  // background card
  doc.setFillColor(...COLORS.lightBg);
  doc.roundedRect(MARGIN_L, y, CONTENT_W, 45, 3, 3, "F");

  doc.setTextColor(...COLORS.text);
  doc.setFontSize(11);
  doc.setFont(undefined, "bold");
  doc.text("Overview", MARGIN_L + 4, y + 7);

  doc.setFontSize(10);
  doc.setFont(undefined, "normal");
  const lines = [
    `This report summarizes QR verification outcomes (Validated / Not Validated) across Products and Geographies.`,
  ];
  for (let i = 0; i < lines.length; i++) {
    const wrapped = doc.splitTextToSize(lines[i], CONTENT_W - 8);
    //auto adjust y based on multiline
    doc.text(wrapped, MARGIN_L + 4, y + 14 + i * 5);
    y = y + (wrapped.length - 1) * 5;
  }
};

const drawSectionTitle = (doc, title, ensureSpace, getY, setY) => {
  ensureSpace(12);
  const y = getY() + 5;

  doc.setFontSize(14);
  doc.setFont(undefined, "bold");
  doc.setTextColor(...COLORS.text);
  doc.text(title, MARGIN_L, y);

  // divider
  doc.setDrawColor(...COLORS.line);
  doc.setLineWidth(0.3);
  doc.line(MARGIN_L, y + 2, PAGE_W - MARGIN_R, y + 2);

  setY(y + 10);
};

const drawKpiCards = (doc, stats, yTop) => {
  const cards = [
    {
      label: "TOTAL SCANS",
      value: `${stats.total}`,
      sub: "All verifications",
      color: COLORS.slate
    },
    {
      label: "VALIDATED",
      value: `${stats.genuine} (${stats.genuinePct}%)`,
      sub: "Verified genuine",
      color: COLORS.green
    },
    {
      label: "NOT VALIDATED",
      value: `${stats.counterfeit} (${stats.counterfeitPct}%)`,
      sub: "Not Verified",
      color: COLORS.red
    },
  ];

  const cardW = 60;
  const cardH = 24;
  const gap = 3;

  let x = MARGIN_L;
  cards.forEach((c) => {
    doc.setFillColor(...c.color);
    doc.roundedRect(x, yTop, cardW, cardH, 3, 3, "F");

    doc.setTextColor(...COLORS.white);

    doc.setFontSize(8);
    doc.setFont(undefined, "bold");
    doc.text(c.label, x + 3, yTop + 6);

    doc.setFontSize(11);
    doc.text(String(c.value), x + 3, yTop + 14);

    doc.setFontSize(7.5);
    doc.setFont(undefined, "normal");
    doc.text(c.sub, x + 3, yTop + 20);

    x += cardW + gap;
  });

  doc.setTextColor(...COLORS.text);
};

const drawDonutWithLegend = (doc, stats, cx, cy) => {
  // donut parameters
  const rOuter = 13;
  const rInner = 7.5;

  const data = [
    { label: "Validated", value: stats.genuine, color: COLORS.green },
    { label: "Not Validated", value: stats.counterfeit, color: COLORS.red }
  ];

  const total = data.reduce((s, d) => s + d.value, 0) || 1;

  // donut arcs (filled wedges)
  let start = 0;
  data.forEach(d => {
    const angle = (d.value / total) * Math.PI * 2;

    doc.setFillColor(...d.color);
    drawPieWedge(doc, cx, cy, rOuter, start, start + angle);

    start += angle;
  });

  // inner circle
  doc.setFillColor(...COLORS.white);
  doc.circle(cx, cy, rInner, "F");

  // center label
  doc.setTextColor(...COLORS.text);

  // legend
  let lx = cx + 18;
  let ly = cy - 3;
  doc.setFontSize(8);
  doc.setFont(undefined, "normal");
  data.forEach(d => {
    doc.setFillColor(...d.color);
    doc.rect(lx, ly - 2.8, 3.2, 3.2, "F");
    doc.setTextColor(...COLORS.text);
    doc.text(`${d.label}: ${d.value}`, lx + 5, ly);
    ly += 6;
  });
};

const drawPieWedge = (doc, cx, cy, r, startAngle, endAngle) => {
  const steps = 36;
  const step = (endAngle - startAngle) / steps;

  for (let i = 0; i < steps; i++) {
    const a1 = startAngle + i * step;
    const a2 = startAngle + (i + 1) * step;
    const x1 = cx + r * Math.cos(a1);
    const y1 = cy + r * Math.sin(a1);
    const x2 = cx + r * Math.cos(a2);
    const y2 = cy + r * Math.sin(a2);

    doc.triangle(cx, cy, x1, y1, x2, y2, "F");
  }
};

const drawInsightCards = (doc, stats, ensureSpace, getY, setY) => {
  const insights = buildInsights(stats);

  const cardH = 18;
  const cardW = CONTENT_W;
  const gap = 4;

  insights.forEach((ins) => {
    ensureSpace(cardH + gap);
    const y = getY();

    doc.setFillColor(...COLORS.lightBg);
    doc.roundedRect(MARGIN_L, y, cardW, cardH, 3, 3, "F");

    // left accent bar
    doc.setFillColor(...ins.accent);
    doc.roundedRect(MARGIN_L, y, 3.2, cardH, 2, 2, "F");

    doc.setTextColor(...COLORS.text);
    doc.setFont(undefined, "bold");
    doc.setFontSize(10);
    doc.text(ins.title, MARGIN_L + 6, y + 7);

    doc.setFont(undefined, "normal");
    doc.setFontSize(8.5);
    const wrapped = doc.splitTextToSize(ins.body, CONTENT_W - 10);
    doc.text(wrapped.slice(0, 2), MARGIN_L + 6, y + 13);

    setY(y + cardH + gap);
  });
};

const buildInsights = (stats) => {
  const risk = stats.counterfeitPct;
  const riskLabel =
    risk >= 10 ? "High" :
      risk >= 3 ? "Medium" : "Low";

  const peakCtry = stats.peakCountry || "-";
  const peakSku = stats.peakSku || "-";

  return [
    {
      accent: risk >= 10 ? COLORS.red : (risk >= 3 ? COLORS.amber : COLORS.green),
      title: `Overall Risk Level: ${riskLabel}`,
      body: `Not Validated rate is ${risk}% of total verifications. Focus on high-risk geographies and Products to reduce exposure and improve field verification outcomes.`
    },
    {
      accent: COLORS.slate,
      title: "Primary Hotspot",
      body: `Highest counterfeit activity is observed in: ${peakCtry}. Prioritize enforcement, distributor validation, and targeted education in this region.`
    },
    {
      accent: COLORS.navy,
      title: "Operational Focus",
      body: `Peak scan volume Product is ${peakSku}. Review labeling, packaging, and distribution integrity for this Product and validate its downstream channels.`
    }
  ];
};

/* =========================================================
   CHARTS
   ========================================================= */
const drawHorizontalStackedBarChart = (doc, title, data, x, y, w) => {

  const barH = 3;
  const barGap = 1.5;
  const skuGap = 3;

  const rowH = barH * 2 + barGap + skuGap;

  const chartH = data.length * rowH;

  doc.setFillColor(...COLORS.lightBg);
  doc.roundedRect(x, y, w, chartH, 3, 3, "F");

  const plotX = x + 4;
  let curY = y + 2;

  const max = Math.max(
    ...data.map(d => Math.max(d.genuine || 0, d.counterfeit || 0)),
    1
  );

  data.forEach((d) => {

    const g = d.genuine || 0;
    const c = d.counterfeit || 0;

    const barMaxWidth = w - 60;

    const gW = (g / max) * barMaxWidth;
    const cW = (c / max) * barMaxWidth;

    const barX = plotX + 50;

    // SKU label
    doc.setFontSize(7);
    doc.setTextColor(...COLORS.text);
    doc.text(d.name, plotX, curY + barH + 2);

    // --- Genuine bar ---
    doc.setFillColor(...COLORS.green);
    doc.rect(barX, curY, gW, barH, "F");

    doc.setTextColor(...COLORS.muted);
    doc.text(g.toString(), barX + gW + 1, curY + barH - 0.5);

    // --- Counterfeit bar ---
    const fakeY = curY + barH + barGap;

    doc.setFillColor(...COLORS.red);
    doc.rect(barX, fakeY, cW, barH, "F");

    doc.text(c.toString(), barX + cW + 1, fakeY + barH - 0.5);

    curY += rowH;
  });

  return chartH;
};

/* =========================================================
   TABLES
   ========================================================= */
const buildTopEntities = (map, limit, scoreFn) => {
  return Object.entries(map)
    .sort((a, b) => scoreFn(b[1]) - scoreFn(a[1]))
    .slice(0, limit);
};

// sort bt total and then by counterfeit if same total
const buildSummaryRows = (map, limit, kind) => {
  const entries = Object.entries(map)
    .sort((a, b) => (b[1].total - a[1].total) || (b[1].counterfeit - a[1].counterfeit))
    .slice(0, limit);

  return entries.map(([name, v]) => {
    const pct = v.total ? Math.round((v.counterfeit / v.total) * 100) : 0;
    const risk = pct >= 10 ? "High" : (pct >= 3 ? "Medium" : "Low");

    return [
      name,
      String(v.total),
      String(v.genuine),
      String(v.counterfeit)
    ];
  });
};

const renderTable = (doc, cfg, ensureSpace, getY, setY) => {
  const {
    title,
    headers,
    rows,
    colW,
    fontSize = 9,
    zebra = true,
    riskColoring = false
  } = cfg;

  // Title block
  ensureSpace(18);
  const startY = getY();

  const tableX = MARGIN_L;
  const tableW = colW.reduce((a, b) => a + b, 0);

  const rowH = 8;

  const drawHeader = () => {
    ensureSpace(rowH + 4);
    const y = getY();

    doc.setFillColor(...COLORS.slate);
    doc.rect(tableX, y - 5, tableW, rowH + 2, "F");

    doc.setTextColor(...COLORS.white);
    doc.setFont(undefined, "bold");
    doc.setFontSize(fontSize);

    let x = tableX;
    headers.forEach((h, i) => {
      doc.text(String(h), x + 1.2, y);
      x += colW[i];
    });

    doc.setTextColor(...COLORS.text);
    doc.setFont(undefined, "normal");

    setY(y + rowH);
  };

  const drawRow = (cells, idx) => {
    ensureSpace(rowH + 2);
    const y = getY();

    // zebra fill
    if (zebra && idx % 2 === 0) {
      doc.setFillColor(250, 251, 253);
      doc.rect(tableX, y - 5, tableW, rowH + 2, "F");
    }

    // risk coloring (right-most "Risk" cell)
    let riskFill = null;
    if (riskColoring && cells && cells.length) {
      const risk = String(cells[cells.length - 1] || "");
      if (risk === "High") riskFill = COLORS.red;
      else if (risk === "Medium") riskFill = COLORS.amber;
      else if (risk === "Low") riskFill = COLORS.green;
    }

    // draw cells
    doc.setFontSize(fontSize);
    let x = tableX;

    cells.forEach((c, i) => {
      const cellText = String(c ?? "");
      const maxW = colW[i] - 2.4;

      const wrapped = doc.splitTextToSize(cellText, maxW);
      doc.text(wrapped.slice(0, 1), x + 1.2, y);
      x += colW[i];
    });

    setY(y + rowH);
  };

  // initial header
  drawHeader();

  // rows
  rows.forEach((r, idx) => {
    const before = getY();
    const broke = ensureSpace(rowH + 2);
    if (broke && getY() < before) {
      // new page => repeat header
      drawHeader();
    }
    drawRow(r, idx);
  });

  setY(getY() + 6);
};

/* =========================================================
   FOOTER
   ========================================================= */
const addFooter = (doc) => {
  const pages = doc.internal.getNumberOfPages();
  for (let i = 1; i <= pages; i++) {
    doc.setPage(i);

    doc.setFontSize(9);
    doc.setTextColor(...COLORS.muted);

    doc.text("Confidential – Internal Use Only", PAGE_W / 2, 286, { align: "center" });
    doc.text(`Page ${i} of ${pages}`, PAGE_W / 2, 291, { align: "center" });

    // branding line
    doc.setFontSize(8);
    doc.text("QRmor Asset Security Analytics Report", PAGE_W - 12, 283, { align: "right" });
    doc.text("© 2026 Scinova Scientifics | Powered by CIIRC®", PAGE_W - 12, 288, { align: "right" });
  }

  doc.setTextColor(...COLORS.text);
};

const STATE_CENTERS = {
  "Andhra Pradesh": [15.9129, 79.7400],
  "Arunachal Pradesh": [28.2180, 94.7278],
  "Assam": [26.2006, 92.9376],
  "Bihar": [25.0961, 85.3131],
  "Chhattisgarh": [21.2787, 81.8661],
  "Delhi": [28.7041, 77.1025],
  "Goa": [15.2993, 74.1240],
  "Gujarat": [22.2587, 71.1924],
  "Haryana": [29.0588, 76.0856],
  "Himachal Pradesh": [31.1048, 77.1734],
  "Jharkhand": [23.6102, 85.2799],
  "Karnataka": [15.3173, 75.7139],
  "Kerala": [10.8505, 76.2711],
  "Madhya Pradesh": [22.9734, 78.6569],
  "Maharashtra": [19.7515, 75.7139],
  "Manipur": [24.6637, 93.9063],
  "Meghalaya": [25.4670, 91.3662],
  "Mizoram": [23.1645, 92.9376],
  "Nagaland": [26.1584, 94.5624],
  "Odisha": [20.9517, 85.0985],
  "Punjab": [31.1471, 75.3412],
  "Rajasthan": [27.0238, 74.2179],
  "Sikkim": [27.5330, 88.5122],
  "Tamil Nadu": [11.1271, 78.6569],
  "Telangana": [18.1124, 79.0193],
  "Tripura": [23.9408, 91.9882],
  "Uttar Pradesh": [26.8467, 80.9462],
  "Uttarakhand": [30.0668, 79.0193],
  "West Bengal": [22.9868, 87.8550]
};

const generatePDF = async (data) => {

  const mapDiv = document.createElement("div");
  mapDiv.style.width = "800px";
  mapDiv.style.height = "800px";
  mapDiv.style.position = "absolute";
  mapDiv.style.left = "-9999px";
  mapDiv.style.top = "0px";

  document.body.appendChild(mapDiv);

  const map = L.map(mapDiv, {
    center: [22.5, 79],
    zoom: 5,
    zoomControl: false,
    attributionControl: false
  });

  L.tileLayer(
    "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png",
    { crossOrigin: true }
  ).addTo(map);

  /* ---------- GROUP BY STATE ---------- */

  const stateClusters = {};

  data
    .filter(p => p.latitude && p.longitude && p.state)
    .forEach(p => {

      const state = p.state.trim();

      if (!stateClusters[state]) {
        stateClusters[state] = {
          count: 0,
          latSum: 0,
          lonSum: 0
        };
      }

      stateClusters[state].count += 1;
      stateClusters[state].latSum += parseFloat(p.latitude);
      stateClusters[state].lonSum += parseFloat(p.longitude);
    });

  /* ---------- CREATE STATE BUBBLES ---------- */

  Object.keys(stateClusters).forEach(state => {

    const s = stateClusters[state];

    if (!STATE_CENTERS[state]) return;

    const [lat, lon] = STATE_CENTERS[state];

    const bubbleIcon = L.divIcon({
      className: "state-cluster",
      html: `
      <div style="
        background:#2c7be5;
        color:white;
        border-radius:50%;
        width:42px;
        height:42px;
        display:flex;
        align-items:center;
        justify-content:center;
        font-weight:bold;
        border:3px solid white;
        box-shadow:0 0 6px rgba(0,0,0,0.4);
      ">
        ${s.count}
      </div>
    `,
      iconSize: [42, 42]
    });

    L.marker([lat, lon], { icon: bubbleIcon })
      .addTo(map)
      .bindTooltip(`${state}: ${s.count} verifications`);

  });

  /* ---------- WAIT FOR TILES ---------- */

  await new Promise(res => setTimeout(res, 2000));

  const canvas = await html2canvas(mapDiv, { useCORS: true });
  const imgData = canvas.toDataURL("image/png");

  map.remove();
  document.body.removeChild(mapDiv);

  return imgData;
};